import React from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useStore from "../../../context/useStore";
import ChoixChips from "../../common/ChoixChips/ChoixChips";
import Control from "../../common/Control";
import { Pill } from "../../common/DataList";
import { FactList, FicheHeader, FicheSection, StickySaveBar, copyToClipboard } from "../../common/Fiche";
import Loader from "../../common/Loader";
import SaveBtn from "../../common/SaveBtn";
import { nomStructure } from "../UserList/userListConfig";
import ChoixSitesCompte from "../Operateurs/ChoixSitesCompte";
import OrganisationsEtSites from "./OrganisationsEtSites";
import useUserDetail from "./useUserDetail";
import {
  faitsUtilisateur,
  libelleRole,
  nomComplet,
  pastillesUtilisateur,
  rolesDisponibles,
  versDateSaisie,
} from "./utilisateurHelpers";
import "./user-detail.scss";

// ============================================================
// Fiche utilisateur (agent du back-office) sur le kit Fiche : en-tête
// d'identité (état du compte, rôle, actions sur le compte), puis trois
// sections — Identité, Rôle et habilitations, Organisations et sites — et
// une barre d'enregistrement collante dès qu'un champ change.
// Mêmes appels et mêmes règles que l'écran historique (useUserDetail).
// Un rôle d'opérateur d'accompagnement (CE-1407) remplace les habilitations
// EP par l'option « alertes de planning » et la section « Organisations et
// sites » par le choix de la structure.
// ============================================================

const HABILITATIONS = [
  {
    name: "isEP",
    label: "Peut participer aux EP",
    aide: "Peut être invité comme membre du jury et accède au calendrier des EP.",
  },
  {
    name: "canStartEP",
    label: "Peut démarrer une EP",
    aide: "Peut ouvrir une session d'EP le jour de sa tenue.",
  },
  {
    name: "isDirector",
    label: "Est délégataire du DG",
    aide: "Accède aux décisions réservées au Directeur de la Caf (suspension / remobilisation).",
  },
];

const UserDetail = (props) => {
  const id = props.match && props.match.params ? props.match.params.id : undefined;
  const [state] = useStore();
  const constantes = Array.isArray(state.constants.items) ? {} : state.constants.items;
  const fiche = useUserDetail({ id, history: props.history });
  const { user, errors, creation } = fiche;

  if (fiche.isLoading && !user.id) return <Loader />;

  const nom = nomComplet(user);
  const titre = creation ? "Nouvel utilisateur" : nom || "Utilisateur";
  const pastilles = pastillesUtilisateur(user, constantes);
  const faits = faitsUtilisateur(user, undefined, { nomStructure: nomStructure(user.operator, fiche.operateurs) });

  const copier = (valeur) =>
    copyToClipboard(valeur).then(
      () => toast.success("La valeur a été copiée dans votre presse-papier"),
      () => toast.error("Impossible de copier la valeur")
    );

  const actions = !creation && user.id ? (
    <>
      <SaveBtn
        margin=""
        type="btn-ghost"
        className="d-center"
        save={fiche.renvoyerActivation}
        isSaving={fiche.isResending}
        text={
          <>
            <i className="fa fa-envelope-open-text mr-2" aria-hidden="true" />
            Renvoyer l'email d'activation
          </>
        }
      />
      <SaveBtn
        margin=""
        type={user.isActivated ? "btn-ghost" : "btn-primary"}
        className="d-center"
        save={fiche.basculerActivation}
        isSaving={fiche.isDisabling}
        color={user.isActivated ? "#0e6bb0" : "#ffffff"}
        text={
          <>
            <i className="fa fa-power-off mr-2" aria-hidden="true" />
            {user.isActivated ? "Désactiver le compte" : "Activer le compte"}
          </>
        }
      />
      {user.isActivated && (
        <SaveBtn
          margin=""
          type="btn-ghost"
          className="d-center"
          save={fiche.debloquer}
          isSaving={fiche.isUnlocking}
          color="#0e6bb0"
          text={
            <>
              <i className="fa fa-lock-open mr-2" aria-hidden="true" />
              Débloquer le compte
            </>
          }
        />
      )}
    </>
  ) : null;

  return (
    <div className="fiche-page container-fluid px-5 my-5 fiche-utilisateur">
      {errors && errors.other && (
        <div className="alert alert-danger" role="alert">
          {errors.other}
        </div>
      )}

      <FicheHeader
        back="/users"
        backLabel="Retour à la liste des utilisateurs"
        eyebrow={creation ? "Utilisateur" : ["Utilisateur", libelleRole(user.role, constantes)].filter(Boolean).join(" · ")}
        title={titre}
        pills={
          pastilles.length
            ? pastilles.map((p) => (
                <Pill key={p.id} tone={p.ton} icon={p.icone}>
                  {p.libelle}
                </Pill>
              ))
            : null
        }
        actions={actions}
      >
        {!creation && (
          <FactList
            facts={faits.map((f) =>
              f.note
                ? {
                    ...f,
                    render: (valeur) => (
                      <>
                        <span className="fact__value">{valeur}</span>
                        <span className="fiche-utilisateur__note cell-note--warn">
                          <i className="fa fa-exclamation-circle" aria-hidden="true" />
                          {f.note}
                        </span>
                      </>
                    ),
                  }
                : f
            )}
            onCopy={copier}
            emptyLabel="Non renseigné"
          />
        )}
      </FicheHeader>

      <div className="fiche-utilisateur__grille">
        <FicheSection id="fiche-utilisateur-identite" title="Identité">
          <div className="fiche-utilisateur__champs">
            <Control
              margin="my-0"
              label="Civilité"
              type="select"
              name="gender"
              value={user.gender}
              datas={constantes.GENDERS || [{ id: 1, name: "Monsieur" }, { id: 2, name: "Madame" }]}
              dataIndex="id"
              dataLabel="name"
              change={fiche.changer}
              error={errors}
            />
            <Control
              margin="my-0"
              label="Prénom"
              name="firstname"
              value={user.firstname}
              change={fiche.changer}
              error={errors}
              inputProps={{ autoComplete: "off" }}
            />
            <Control
              margin="my-0"
              label="Nom"
              name="lastname"
              value={user.lastname}
              change={fiche.changer}
              error={errors}
              inputProps={{ autoComplete: "off" }}
            />
            <Control
              margin="my-0"
              label="Email"
              name="email"
              type="email"
              value={user.email}
              change={fiche.changer}
              error={errors}
              inputProps={{ autoComplete: "off", inputMode: "email" }}
            />
            <Control
              margin="my-0"
              label="Téléphone"
              name="phone"
              type="tel"
              value={user.phone}
              change={fiche.changer}
              error={errors}
              inputProps={{ inputMode: "tel" }}
            />
            <Control
              margin="my-0"
              label="Date de fin de contrat"
              name="contractEndAt"
              type="date"
              value={versDateSaisie(user.contractEndAt)}
              change={fiche.changer}
              error={errors}
            />
            {creation && !fiche.compteOperateur && (
              <Control
                margin="my-0"
                label="Mot de passe"
                name="password"
                type="password"
                value={user.password || ""}
                change={fiche.changer}
                error={errors}
              />
            )}
            {creation && fiche.compteOperateur && (
              <p className="habilitation__aide ml-0 mb-0">
                Pas de mot de passe à saisir : l'utilisateur choisira le sien en activant son compte.
              </p>
            )}
          </div>
        </FicheSection>

        <FicheSection id="fiche-utilisateur-habilitations" title="Rôle et habilitations">
          <ChoixChips
            name="role"
            legend="Rôle"
            options={rolesDisponibles(constantes)}
            value={user.role}
            onChange={(valeur) => fiche.modifierChamp("role", valeur)}
            error={errors.role}
          />
          {fiche.compteOperateur ? (
            <ul className="habilitations">
              <li className="habilitation">
                <Control
                  margin="my-0"
                  type="checkbox"
                  name="receivesPlanningAlerts"
                  label="Reçoit les alertes de planning"
                  checked={!!user.receivesPlanningAlerts}
                  change={fiche.changer}
                  error={errors}
                />
                <p className="habilitation__aide">
                  Courriel quand aucun créneau n'est disponible pour positionner un allocataire.
                </p>
              </li>
            </ul>
          ) : (
            <ul className="habilitations">
              {HABILITATIONS.map((h) => (
                <li key={h.name} className="habilitation">
                  <Control
                    margin="my-0"
                    type="checkbox"
                    name={h.name}
                    label={h.label}
                    checked={!!user[h.name]}
                    change={fiche.changer}
                    error={errors}
                  />
                  <p className="habilitation__aide">{h.aide}</p>
                </li>
              ))}
            </ul>
          )}
        </FicheSection>
      </div>

      {fiche.compteOperateur ? (
        <FicheSection id="fiche-utilisateur-structure" title="Structure">
          <div className="fiche-utilisateur__champs">
            <Control
              margin="my-0"
              label="Opérateur d'accompagnement"
              type="select"
              name="operator"
              value={user.operator || ""}
              datas={fiche.operateurs || []}
              dataIndex="id"
              dataLabel="name"
              selectFirstLabel="Choisir la structure…"
              change={fiche.changer}
              error={errors}
              required
            />
          </div>
          <p className="habilitation__aide mt-2 ml-0">
            Le compte n'appartient qu'à cette structure, et n'en voit que les sites cochés.
          </p>
          {user.operator && (
            <div className="fiche-utilisateur__sites-structure">
              {fiche.sitesStructureEnChargement ? (
                <p className="habilitation__aide ml-0 mb-0">Chargement des sites de la structure…</p>
              ) : (
                <ChoixSitesCompte
                  sites={fiche.sitesStructure}
                  selection={user.sites.filter((id) => fiche.sitesStructure.some((s) => s.id === id))}
                  onChange={(ids) => fiche.modifierChamp("sites", ids)}
                  nomStructure={nomStructure(user.operator, fiche.operateurs)}
                  erreur={errors.sites}
                  idPrefix="fiche-utilisateur"
                />
              )}
            </div>
          )}
          {!creation && user.id && user.operator && fiche.structureChargee === user.operator && (
            <p className="mt-2 mb-0">
              <Link to={`/operateurs/${user.operator}/comptes/${user.id}`}>
                <i className="fa fa-arrow-right mr-2" aria-hidden="true" />
                Voir ses droits depuis sa structure
              </Link>
            </p>
          )}
        </FicheSection>
      ) : (
        <FicheSection
          id="fiche-utilisateur-rattachements"
          title="Organisations et sites"
          actions={
            <span className="fiche-utilisateur__resume">
              {user.organizations.length} organisation{user.organizations.length > 1 ? "s" : ""} ·{" "}
              {user.sites.length} site{user.sites.length > 1 ? "s" : ""}
            </span>
          }
        >
          <OrganisationsEtSites
            organizations={fiche.organizations || []}
            sites={fiche.sites}
            selectionOrganisations={user.organizations}
            selectionSites={user.sites}
            organisationsEnChargement={fiche.organisationsEnChargement}
            onBasculerOrganisation={fiche.basculerOrganisation}
            onBasculerSite={fiche.basculerSite}
            onDefinirSitesDe={fiche.definirSitesDe}
            blocage={fiche.blocage}
            onFermerBlocage={fiche.fermerBlocage}
            erreurs={errors}
          />
        </FicheSection>
      )}

      <div className="fiche-panel__footer">
        <SaveBtn
          margin=""
          type="btn-primary"
          className="py-3 px-4 align-items-center justify-content-center"
          text={creation ? "Créer l'utilisateur" : "Enregistrer"}
          save={fiche.enregistrer}
          isSaving={fiche.isSaving}
          isSaved={false}
        />
      </div>
      <StickySaveBar
        visible={fiche.modifie}
        isSaving={fiche.isSaving}
        onSave={fiche.enregistrer}
        message="Modifications non enregistrées"
        label={creation ? "Créer l'utilisateur" : "Enregistrer"}
      />
    </div>
  );
};

export default UserDetail;
